/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import {
    gnoment,
    rangeValueToGnomentRange,
    gnomentToCalendarDate,
} from 'shared-ui/gnoment';

import { localToday, Button, Icon, Pagination, DateRangePicker, Box } from 'sarsaparilla';

import { MONTH_DAY_YEAR_NUM_FORMAT } from '../../constants';
import {
    StatusDetails,
    filterLotteriesBetweenDates,
} from '../../utils/lottery-schedule-utils';

const iconHelp = <Icon iconName="help" />;
const TABLE_LENGTH = 14;
const tableColumns = [
    {
        id: 'trip_date',
        name: 'Trip Date',
    },
    {
        id: 'lottery_opens',
        name: 'Lottery Opens',
    },
    {
        id: 'lottery_closes',
        name: 'Lottery Closes',
    },
    {
        id: 'results_available',
        name: 'Results Available',
    },
    {
        id: 'status',
        name: 'Status',
    },
    {
        id: 'available_quota',
        name: 'Available',
    },
    {
        id: 'actions',
        name: 'Actions',
    },
];

const DATE_FORMAT = 'MM/DD/YYYY [|] h:mm:ss a';

export default class LotteryScheduleTable extends Component {
    static propTypes = {
        permit: PropTypes.object,
        dailyLotteries: PropTypes.array,
        onStatusModalClicked: PropTypes.func,
    };

    state = {
        activePage: 0,
        filteredLotteries: this.props.dailyLotteries || [],
        start: null,
        end: null,
    };

    shouldComponentUpdate(nextProps, nextState) {
        const { permit, dailyLotteries } = this.props;
        const { permit: nextPermit, dailyLotteries: nextDailyLotteries } = nextProps;

        const { filteredLotteries, activePage } = this.state;
        const { filteredLotteries: nextFilteredLotteries, activePage: nextActivePage } =
            nextState;

        // only length needs to be checked since it should only be updating once...
        const dailyLotteriesChange =
            get(dailyLotteries, 'length') !== get(nextDailyLotteries, 'length');
        const permitIdChange = get(permit, 'id') !== get(nextPermit, 'id');

        const filteredLotteriesChange =
            get(filteredLotteries, 'length') !== get(nextFilteredLotteries, 'length');
        const activePageChange = activePage !== nextActivePage;

        if (
            dailyLotteriesChange ||
            permitIdChange ||
            filteredLotteriesChange ||
            activePageChange
        ) {
            return true;
        }
        return false;
    }

    componentDidUpdate(prevProps) {
        // updateLotteries when receiving lotteries from the api
        if (
            get(prevProps, 'dailyLotteries.length') !==
                get(this.props, 'dailyLotteries.length') ||
            get(prevProps, 'permit.id') !== get(this.props, 'permit.id')
        ) {
            /* eslint-disable-next-line react/no-did-update-set-state */
            this.setState({ filteredLotteries: this.props.dailyLotteries });
        }
    }

    onDatePickerChange = ({ startDate, endDate }) => {
        const { dailyLotteries, permit } = this.props;

        this.setState({ start: startDate, end: endDate, activePage: 0 });

        // update filteredLotteries
        if (!isEmpty(dailyLotteries)) {
            const startDateInPermitTZ = startDate
                ? gnoment(startDate)
                      .utc()
                      .startOf('day')
                      .tz(get(permit, 'time_zone'), true)
                : null;
            const endDateInPermitTZ = endDate
                ? gnoment(endDate).utc().startOf('day').tz(get(permit, 'time_zone'), true)
                : null;

            const filteredLotteries = filterLotteriesBetweenDates({
                lotteries: dailyLotteries,
                startDate: startDateInPermitTZ,
                endDate: endDateInPermitTZ,
            });
            this.setState({ filteredLotteries });
        }
    };

    onActivePageChanged = (pageNumber) => this.setState({ activePage: pageNumber });

    getDatesScreenReaderText(tripDate, columnDate) {
        const tripDateScreenReaderText = tripDate.format('LL');
        const columnDateScreenReaderText = columnDate.format('LLL');
        return `${columnDateScreenReaderText} for trip date ${tripDateScreenReaderText}`;
    }

    renderPagination() {
        const { activePage, filteredLotteries } = this.state;

        const props = {
            activePage,
            pages: Math.ceil(filteredLotteries.length / TABLE_LENGTH),
            onChange: this.onActivePageChanged,
        };

        const entryStart = activePage * TABLE_LENGTH + 1;
        let entryEnd = entryStart + TABLE_LENGTH - 1;
        // end of table length can't be more than total lotteries
        if (entryEnd > filteredLotteries.length) {
            entryEnd = filteredLotteries.length;
        }

        return (
            <div className="lottery-schedule-table-pagination">
                <p>{`${entryStart} - ${entryEnd} of ${filteredLotteries.length} entries`}</p>
                <Pagination {...props} />
            </div>
        );
    }

    render() {
        const { onStatusModalClicked } = this.props;
        const { filteredLotteries, activePage, start, end } = this.state;

        const lotteryOffset = activePage * TABLE_LENGTH;
        const activeLotteries = filteredLotteries.slice(
            lotteryOffset,
            lotteryOffset + TABLE_LENGTH
        );

        let datePickerValue = null;

        if (start && end) {
            datePickerValue = {
                start: gnomentToCalendarDate(start),
                end: gnomentToCalendarDate(end),
            };
        }

        return (
            <>
                <div className="row-wrap">
                    <DateRangePicker
                        id="lottery-schedule-date-range"
                        label="What day are you interested in going?"
                        value={datePickerValue}
                        onChange={(value) => {
                            this.onDatePickerChange(rangeValueToGnomentRange(value));
                        }}
                        minValue={localToday}
                    />

                    <Button
                        className="lottery-schedule-status-button"
                        appearance="link"
                        iconBeforeElement={iconHelp}
                        onClick={onStatusModalClicked}
                    >
                        What do the statuses mean?
                    </Button>
                </div>
                <div className="lottery-table-wrapper">
                    <table
                        className="lottery-table"
                        role="grid"
                        aria-label="Lottery Schedule Table"
                    >
                        <thead>
                            <tr>
                                {tableColumns.map(({ name: colName, id }) => (
                                    <th scope="col" key={id} id={id}>
                                        {colName}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {activeLotteries.map(
                                ({
                                    id,
                                    trip_date: tripDate,
                                    lottery_opens: lotteryOpens,
                                    lottery_closes: lotteryCloses,
                                    results_available: resultsAvailable,
                                    status,
                                    available_quota: availableQuota,
                                    actions,
                                }) => {
                                    return (
                                        <tr key={id}>
                                            <td className="light-bold">
                                                <span className="rec-sr-only">{`Lottery trip date is on ${tripDate.format('LL')}`}</span>
                                                <span aria-hidden="true">
                                                    {tripDate.format(
                                                        MONTH_DAY_YEAR_NUM_FORMAT
                                                    )}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="rec-sr-only">{`Lottery opens on ${this.getDatesScreenReaderText(tripDate, lotteryOpens)}`}</span>
                                                <span aria-hidden="true">
                                                    {lotteryOpens.format(DATE_FORMAT)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="rec-sr-only">{`Lottery closes on ${this.getDatesScreenReaderText(tripDate, lotteryCloses)}`}</span>
                                                <span aria-hidden="true">
                                                    {lotteryCloses.format(DATE_FORMAT)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="rec-sr-only">{`Results are available on ${this.getDatesScreenReaderText(tripDate, resultsAvailable)}`}</span>
                                                <span aria-hidden="true">
                                                    {resultsAvailable.format(DATE_FORMAT)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="rec-sr-only">{`Lottery for trip date ${tripDate.format('LL')} is ${status}`}</span>
                                                <div className="status">
                                                    <div
                                                        className={
                                                            StatusDetails[status].color
                                                        }
                                                    />
                                                    <span aria-hidden="true">
                                                        {status}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="rec-sr-only">{`Lottery for trip date ${tripDate.format('LL')} has ${availableQuota} available`}</span>
                                                <span aria-hidden="true">
                                                    {availableQuota}
                                                </span>
                                            </td>
                                            <td>
                                                {!isEmpty(actions) && (
                                                    <Button
                                                        to={get(actions, 'link')}
                                                        appearance="link"
                                                        aria-label={get(
                                                            actions,
                                                            'screenReaderText'
                                                        )}
                                                    >
                                                        {get(actions, 'name')}
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                }
                            )}
                        </tbody>
                    </table>
                </div>
                {this.renderPagination()}
            </>
        );
    }
}
